import React from 'react';
import Info from '../Info';
import Button from '../Button';
import GenPTDashboardIcon from '../SvgIcons/GenPTDashboardIcon';
import { useAppMode } from '../../Providers/AppModeProvider';

const TrialSummary = () => {
  const { trialStats } = useAppMode();

  const summaryMetrics = [
    { title: 'Findings Identified', value: trialStats?.total_findings ?? '0', color: 'text-[#F65C5C]' },
    { title: 'Critical Findings', value: trialStats?.critical_findings ?? '0', color: 'text-[#F65C5C]' },
    { title: 'Tests Performed', value: `${trialStats?.attempted_tests ?? 0}/${trialStats?.total_attempts ?? 0}`, color: 'text-[#3ADA7A]' },
    { title: 'Assets Tested', value: trialStats?.assets ?? '0', color: 'text-[#228EBC]' },
  ];

  const highlights = [
    {
      title: 'Automated Testing & Triage',
      description: 'GenPT automates deep, continuous testing and intelligently triages findings, eliminating repetitive tasks like script execution and result validation. This frees up your security experts to focus on high-impact strategic initiatives instead of manual, time-consuming work.',
      value: '60 - 90',
      unit: 'Approx Hours',
      tooltip: 'Im a tooltip',
       img: '/images/promotions/flash-timer.svg'
    },
    {
      title: 'Manual Effort Cost Saved',
      description: 'By automating the full cycle of testing, validation, and evidence collection, GenPT significantly cuts down on the hidden labor costs of security assessments. Reallocate your security budget and expert talent from tedious manual tasks to critical, high-value strategic defense.',
      value: '20k - 50K',
      unit: 'Approx Dollars',
      tooltip: 'Im a tooltip',
       img: '/images/promotions/flash-timer.svg'
    },
    {
      title: 'Potential Breach Loss Avoided',
      description: 'GenPT uncovers critical vulnerabilities that traditional methods can miss, helping you proactively fix weaknesses before they are exploited. Strengthen your security posture to avoid the devastating financial and reputational costs associated with a data breach.',
      value: '$80K +',
      unit: 'Approx Dollars',
      tooltip: 'Im a tooltip',
       img: '/images/promotions/computer.svg'
    },
  ];

  const threats = [
    {
      title: '⚠️ Cross-Site Scripting (XSS) on /search-results',
      description:
        'Allows attackers to hijack sessions, steal data, and inject malicious scripts, compromising user trust and safety.',
    },
    {
      title: '⚠️ Remote Code Execution (RCE)',
      description:
        'Enables a full system takeover, allowing attackers to execute commands to steal data, disrupt services, or launch other attacks.',
    },
    {
      title: '⚠️ Denial of Service (DDoS) Risk',
      description:
        'A flood against exposed endpoints can crash your applications, bringing revenue to a complete standstill.',
    },
  ];

  const stats = [
    {
      title: '$4.88M - Average breach cost in 2024',
      description:
        'In 2024, the average data breach cost rose to $4.88 million, a 10 percent increase from the previous year.',
        img: '/images/promotions/graph.png'
    },
    {
      title: '258 days - To detect and contain a Breach',
      description:
        'Organizations took an average of 258 days to identify and contain a breach, giving attackers extended system access.',
         img: '/images/promotions/clock.png'
    },
  ];

  const features = [
    {
      title: 'AI Driven Strategic Insights',
      description:
        'Get actionable insights enriched with context. Prioritize faster, understand root causes clearly, and make smarter decisions with less effort.',
         img: '/images/promotions/aiso.png'
    },
    {
      title: 'Unlimited Testing Coverage',
      description:
        'Run unlimited authenticated and unauthenticated tests across all your assets. No restrictions or blind spots, just complete and continuous visibility.',
         img: '/images/promotions/target.png'
    },
    {
      title: 'One-Click Reporting',
      description:
        'Instantly generate detailed reports, from executive summaries to developer remediation guidance. Save hours of manual work with a single tap.',
         img: '/images/promotions/compliance.png'
    },
  ];

  return (
    <div className="bg-white min-h-[84vh] p-4 rounded-xl">
      {/* Header Section */}
      <div
        className="p-4 rounded-xl"
        style={{
          background:
            'linear-gradient(88.04deg, rgba(18, 175, 184, 0.1) 27.58%, rgba(98, 199, 227, 0.1) 51.58%, rgba(254, 164, 43, 0.1) 93.45%)',
        }}
      >
        <div className="flex gap-2 items-center">
          <GenPTDashboardIcon />
        </div>
        <div className="mt-2 font-bold text-xl">
          Well done! You've laid the groundwork for continuous, proactive security.
        </div>
        <div className="mt-1 text-sm">
          Thank you for trying GenPT. Your trial period just ended, and here is the impact you have
          made.
        </div>
        <Button
          className="mt-4"
          onClick={() => window.open('https://www.siemba.io/book-a-demo', '_blank')}
        >
          Schedule Your Upgrade Call
        </Button>

        {/* Summary Cards */}
        <div className="mt-4">
          <div className="font-bold text-secondary-600">GenPT Trial Summary</div>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {summaryMetrics.map((item, index) => (
              <div
                key={index}
                className="bg-white text-center rounded-xl flex flex-col items-center min-w-[160px] p-2"
              >
                <div className={`font-bold text-xl ${item.color}`}>{item.value}</div>
                <div className="font-semibold text-sm">{item.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Highlights */}
      <div className="mt-8">
        <div className="font-bold text-xl">Your Potential ROI with GenPT</div>
        <div className="mt-4 flex flex-col gap-3">
          {highlights.map((highlight, index) => (
            <div key={index} className="relative">
              {/* <div className="absolute top-2 right-2">
                <Info tooltip={highlight.tooltip} />
              </div> */}
              <div className="border-l-[8px] border-tertiary-600 bg-[rgba(255,243,224,0.8)] p-4 rounded-lg flex items-center">
                <div className="basis-[70%]">
                  <div className="text-lg font-semibold flex items-center gap-2">
                    <img className="h-6 w-6" src={highlight.img} alt={highlight.title} />
                    <div>{highlight.title}</div>
                  </div>
                  <div className="mt-2 text-sm">{highlight.description}</div>
                </div>
                <div className="basis-[30%] flex items-center justify-center">
                  <div className="bg-white flex flex-col items-center justify-center rounded-2xl p-4">
                    <div className="font-bold text-2xl">{highlight.value}</div>
                    <div>{highlight.unit}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Threats */}
      <div className="mt-8">
        <div className="font-bold text-xl">
        🚨 Understanding Your Exposure: Common High-Impact Threats
        </div>
        <div className="mt-2 text-sm">
        GenPT goes beyond surface-level scans to identify high-impact vulnerabilities that could expose your business to serious risk. Here are examples of common critical threats we help you find and neutralize before they can be exploited.
        </div>
        <div className="mt-4 flex flex-col gap-3">
          {threats.map((threat, index) => (
            <div key={index} className="bg-[rgba(246,92,92,0.2)] p-4 rounded-lg">
              <div className="text-lg font-semibold">{threat.title}</div>
              <div className="mt-2 text-sm">{threat.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8">
        <div className="font-bold text-xl">
        These aren't just technical flaws, they're business liabilities.
        </div>
        <div className="mt-2 text-sm">
          Unchecked vulnerabilities can lead to data loss, downtime, reputational damage, and
          regulatory penalties. Full GenPT coverage is a strategic investment in protecting your
          business, earning customer trust, and ensuring long-term resilience.
        </div>
        <div className="mt-4 flex items-center gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="p-4 rounded-xl bg-[#E0F8F7] shadow-lg flex-1">
              <div className="w-14 h-14 p-2 bg-white rounded-full flex items-center justify-center">
                <img src={stat.img} alt={stat.title} />
              </div>
              <div className="mt-4 text-lg font-semibold">{stat.title}</div>
              <div className="mt-2 text-sm">{stat.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="mt-8">
        <div className="font-bold text-xl">Keep Your Security Advantage Growing</div>
        <div className="mt-2 text-sm">
          Unlock everything GenPT has to offer and move from trial to transformation.
        </div>
        <div className="mt-4 flex items-center gap-4">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-4 rounded-xl"
              style={{
                background:
                  'linear-gradient(131.24deg, #E0F8F7 3.39%, #E0F8F7 62.46%, #56C2DB 119.59%)',
              }}
            >
              <div className="w-14 h-14 p-2 bg-white rounded-full flex items-center justify-center">
              <img src={feature.img} alt={feature.title} />
              </div>
              <div className="mt-4 text-lg font-semibold">{feature.title}</div>
              <div className="mt-2 text-sm">{feature.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrialSummary;
